import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import usePasswords from "../hooks/usePasswords";
import { UIState } from "../types/ui-types";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";

type Inputs = {
  password: string;
};

const MainLayoutContainer = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--sumi-sidenav-background);

  a {
    color: var(--text-color);
    text-decoration: underline;
    cursor: pointer;
  }

  h1 {
    font-size: 26px;
    margin-top: 2rem;
    color: var(--sumi-text-primary);
  }

  & label {
    font-size: 12px;
  }
`;

const Form = styled.div`
  width: 90%;
  max-width: 450px;
  padding: 3rem;
  background-color: var(--blue-200);
  color: var(--text-color);
  border-radius: 5px;
`;

const InputContainer = styled.div`
  width: 100%;
  height: 105px;
  text-align: left;

  & .p-inputtext {
    height: 56px;
  }

  & div,
  .p-float-label,
  .p-inputtext,
  .p-password,
  .p-button {
    width: 100%;
  }
`;

const GappedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: justify;
`;

export const ChangePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [toast, tempToken, tempUserId, setTempToken, setTempUserId, theme] =
    useUIStore(
      useShallow((state: UIState) => [
        state.toastRef,
        state.tempToken,
        state.tempUserId,
        state.setTempToken,
        state.setTempUserId,
        state.theme,
      ])
    );

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("id") && params.get("token")) {
      setTempUserId(parseInt(params.get("id") as string, 10));
      setTempToken(params.get("token") as string);

      const url = new URL(window.location.href);
      url.searchParams.delete("id");
      url.searchParams.delete("token");
      window.history.replaceState(
        {},
        document.title,
        url.pathname + url.search
      );
    }
  }, [location.search]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: { password: "" },
  });

  const {
    changeUserPassword,
    isChangingPassword,
    validateUserToken,
    isValidatingToken,
  } = usePasswords();
  const handleChangePassword = async (data: Inputs) => {
    // Implement password change logic here, using id and token

    if (!tempUserId || !tempToken) {
      toast?.current?.show({
        severity: "error",
        detail: t("text-invalid-password-reset-link"),
        life: 3000,
      });
      return;
    }

    const result = await validateUserToken({
      userId: tempUserId,
      token: tempToken,
    });

    if (!result.valid) {
      toast?.current?.show({
        severity: "error",
        summary: result?.newLinkSent ? t("error-token-expired-summary") : "",
        detail: result.newLinkSent
          ? t("error-token-expired-detail")
          : t("text-password-change-failed"),
        life: 15000,
        closable: true,
      });
      return;
    }

    if (result?.valid) {
      await changeUserPassword({
        userId: tempUserId,
        changePasswordToken: tempToken,
        newPassword: data.password,
        newPasswordRetyped: data.password,
      });
    }
  };

  const getFormErrorMessage = (name: string) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <></>
    );
  };

  return (
    <MainLayoutContainer className="change-password-screen">
      <img
        src={theme === "dark" ? "/sumion-white.svg" : "/sumion-blue.svg"}
        alt="Sumi Logo"
        style={{ marginBottom: "1rem", width: "156px" }}
      />

      <h1>{t("header-change-password")}</h1>
      <Form>
        <form onSubmit={handleSubmit(handleChangePassword)}>
          <GappedContainer>
            <Controller
              name="password"
              control={control}
              rules={{
                required: t("warning-required"),
                minLength: {
                  value: 8,
                  message: t("warning-password-minlength"),
                },
              }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-new-password")}</label>
                  <Password
                    id={field.name}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={fieldState.error ? "p-invalid" : ""}
                    placeholder={t("label-new-password")}
                    toggleMask
                    promptLabel={t("label-give-password")}
                    weakLabel={t("label-weak-password")}
                    mediumLabel={t("label-medium-password")}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />
            <InputContainer style={{ height: "75px" }}>
              <Button
                id="change-password-button"
                type="submit"
                label={t("cmd-confirm")}
                style={{ height: "50px" }}
                loading={isChangingPassword || isValidatingToken}
              />
            </InputContainer>
            <InputContainer style={{ height: "25px", textAlign: "center" }}>
              <a
                id="return-to-login-link"
                onClick={() => navigate("/login")}
                style={{ color: "black" }}
              >
                {t("label-navigate-to-login")}
              </a>
            </InputContainer>
          </GappedContainer>
        </form>
      </Form>
    </MainLayoutContainer>
  );
};
